import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';

import { ManagementContactClassificationDtoIn, ClassificationsQueryParams } from '@/models/in/ManagementContactClassificationDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { usePostAuthApiInfiniteQuery } from '@/hooks/UseApiCalls';
import ListShortenedByCount from '@/components/ui/ListShortenedByCount/ListShortenedByCount';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import FiltersArea, { getClassificationStatusGroups } from './FiltersArea';
import useTableDensity from '@/hooks/useTableDensity';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const DENSITY_STORAGE_KEY = 'table.density.classificationsManagement';

// Map column IDs to backend sort field names
const SORT_FIELD_MAP: Record<string, string> = {
  supported: 'ContactName',
  fundId: 'FundId',
  lastRequestDate: 'LastRequestDate',
  activeClassificationsNames: 'ActiveClassificationsNames',
  pendingClassificationsNames: 'PendingClassificationsNames',
};

const ClassificationsManagementPage = () => {
  const { t } = useTranslation();
  // State for the selected row in the table (summary data)
  const [selectedRow, setSelectedRow] = useState<ManagementContactClassificationDtoIn>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  // Page size state
  const [pageSize, setPageSize] = useState<number>(50);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastRequestDate', desc: true }]);

  // Filter state - default to "Pending Approval"
  const [statusFilters, setStatusFilters] = useState<string[]>(['PendingApproval']);

  const statusGroups = useMemo(() => getClassificationStatusGroups(), []);

  const expandedStatusFilters = useMemo(() => {
    return statusFilters.flatMap((filterId) => {
      const group = statusGroups.find((g) => g.id === filterId);
      return group ? group.statuses : [filterId];
    });
  }, [statusFilters, statusGroups]);

  const queryParams: ClassificationsQueryParams = useMemo(
    () => ({
      sortBy: SORT_FIELD_MAP[sorting[0]?.id] || 'LastRequestDate',
      sortDirection: sorting[0]?.desc ? 'Desc' : 'Asc',
      statusFilters: expandedStatusFilters,
      searchTerm: debouncedSearch || undefined,
    }),
    [sorting, expandedStatusFilters, debouncedSearch]
  );

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePostAuthApiInfiniteQuery<ManagementContactClassificationDtoIn, ClassificationsQueryParams>('management/classifications', queryParams, pageSize);

  const accumulatedData = useMemo(() => response?.pages.flatMap((page) => page.data) ?? [], [response]);
  const statusCounts = response?.pages[0]?.statusCounts;

  // Refetch data when page size changes
  useEffect(() => {
    refetch();
  }, [pageSize, refetch]);

  const handleRowClick = useCallback((row: ManagementContactClassificationDtoIn) => {
    setSelectedRow(row);
  }, []);

  const handleCloseBlade = useCallback(() => {
    setSelectedRow(undefined);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Sorting change handler
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  const columns: ColumnDef<ManagementContactClassificationDtoIn>[] = [
    {
      id: 'supported',
      header: Col(t('supported')),
      accessorFn: (row) => ({
        firstName: row.contact.firstName,
        lastName: row.contact.lastName,
        israeliId: row.contact.israeliId,
      }),
      cell: Cell(cellType.SupportedWithIsraeliID),
      enableSorting: true,
      meta: {
        className: 'max-w-[200px] min-w-[200px] w-[200px]',
      },
    },
    {
      id: 'fundId',
      header: Col(t('fundId')),
      cell: Cell(cellType.Number),
      accessorKey: 'fundId',
      enableSorting: true,
      meta: {
        className: 'max-w-[80px] min-w-[80px] w-[80px]',
      },
    },
    {
      id: 'activeClassificationsNames',
      header: Col(t('activeClassifications')),
      accessorKey: 'activeClassificationNames',
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const names = getValue() as string[];
        if (!names || names.length === 0) {
          return <div>-</div>;
        }
        const firstValue = t(names[0]);
        const additionalCount = names.length > 1 ? names.length - 1 : 0;
        const tooltipContent = additionalCount > 0 ? (
          <div className="body-xs-sm-regular font-body-1">
            {names.map(name => t(name)).join(', ')}
          </div>
        ) : null;
        return (
          <ListShortenedByCount
            value={firstValue}
            count={additionalCount}
            tooltipId={additionalCount > 0 ? `active-classification-${row.original.contactId}-${row.original.fundId}` : undefined}
            tooltipContent={tooltipContent}
          />
        );
      },
      meta: {
        className: 'max-w-[130px] min-w-[130px] w-[130px]',
      },
    },
    {
      id: 'pendingClassificationsNames',
      header: Col(t('pendingClassifications')),
      accessorKey: 'pendingClassificationNames',
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const names = getValue() as string[];
        if (!names || names.length === 0) {
          return <div>-</div>;
        }
        const firstValue = t(names[0]);
        const additionalCount = names.length > 1 ? names.length - 1 : 0;
        const tooltipContent = additionalCount > 0 ? (
          <div className="body-xs-sm-regular font-body-1">
            {names.map(name => t(name)).join(', ')}
          </div>
        ) : null;
        return (
          <ListShortenedByCount
            value={firstValue}
            count={additionalCount}
            tooltipId={additionalCount > 0 ? `pending-classification-${row.original.contactId}-${row.original.fundId}` : undefined}
            tooltipContent={tooltipContent}
          />
        );
      },
      meta: {
        className: 'max-w-[130px] min-w-[130px] w-[130px]',
      },
    },
    {
      id: 'lastRequestDate',
      header: Col(t('lastRequestDate')),
      cell: Cell(cellType.Date),
      accessorKey: 'lastRequestDate',
      enableSorting: true,
      meta: {
        className: 'max-w-[127px] min-w-[127px] w-[127px]',
      },
    },
    {
      id: 'childrenCount',
      header: Col(t('childrenCount')),
      accessorKey: 'childrenCount',
      cell: Cell(cellType.Number),
      enableSorting: false,
      meta: {
        className: 'max-w-[70px] min-w-[70px] w-[70px]',
      },
    },
  ];

  return (
    <div>
      {selectedRow && (
        <AdditionalInfo
          contactId={selectedRow.contactId}
          fundId={selectedRow.fundId}
          contactName={`${selectedRow.contact.firstName} ${selectedRow.contact.lastName}`}
          israeliId={selectedRow.contact.israeliId}
          handleXclick={handleCloseBlade}
          onRefresh={handleRefresh}
        />
      )}
      <DataTable
        title={t('classificationsManagement.title')}
        data={accumulatedData}
        isLoading={isLoading || (isFetching && !isFetchingNextPage)}
        columns={columns}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="classificationsManagement"
        searchPlaceholder="classificationsManagement.searchSupported"
        searchTooltip="searchSupportedTooltip"
        sortBy={sorting[0]}
        filters={
          <FiltersArea 
            selectedFilters={statusFilters} 
            onFiltersChange={setStatusFilters} 
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            statusCounts={statusCounts}
          />
        }
        customGlobalSearch={() => true}
        handleRowClick={handleRowClick}
        activeRecord={selectedRow}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        loadingMoreText={t('loadingMoreRecords')}
        manualSorting={true}
        onSortingChange={handleSortingChange}
      />
    </div>
  );
};

export default ClassificationsManagementPage;
